import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { CreateNewsletter, UpdateNewsletter } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useNewsletters(clubId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.newsletters(clubId ?? ""),
		queryFn: () => clubApi.newsletters.list(clubId!),
		enabled: !!clubId,
	});
}

export function useNewsletterDetail(clubId: string | null, nlId: string) {
	return useQuery({
		queryKey: queryKeys.clubPortal.newsletterDetail(clubId ?? "", nlId),
		queryFn: () => clubApi.newsletters.get(clubId!, nlId),
		enabled: !!clubId && !!nlId,
	});
}

export function useCreateNewsletter(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateNewsletter) => clubApi.newsletters.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "newsletters"],
			}),
	});
}

export function useUpdateNewsletter(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ nlId, data }: { nlId: string; data: UpdateNewsletter }) =>
			clubApi.newsletters.update(clubId, nlId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "newsletters"],
			}),
	});
}

export function useDeleteNewsletter(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (nlId: string) => clubApi.newsletters.delete(clubId, nlId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "newsletters"],
			}),
	});
}

export function useSendNewsletter(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (nlId: string) => clubApi.newsletters.send(clubId, nlId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "newsletters"],
			}),
	});
}
